import React, { useState, useEffect } from "react";
import { userService } from "./services/api";
import { extractUserIdFromPaymentUrl } from "./utils/url";

const validateEmail = (email: string) => /\S+@\S+\.\S+/.test(email);
const validateName = (name: string) => /^[A-Za-z]{2,}$/.test(name);

// const triggerStripeCheckout = () => {
//   // Placeholder for Stripe integration
//   alert("Stripe Checkout triggered!");
// };

const App: React.FC = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState({
    firstName: false,
    lastName: false,
    email: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  // Example: get the current URL (if running in browser)
  const paymentUrl = window.location.href;
  const userId = extractUserIdFromPaymentUrl(paymentUrl);
  console.log("Extracted userId:", userId);
  console.log("myname");

  useEffect(() => {
    if (!userId) {
      setError("User ID not found in URL.");
      return;
    }
    const fetchUserData = async () => {
      try {
        const userData = await userService.getUser(userId);
        setFirstName(userData.first_name);
        setLastName(userData.last_name);
        setEmail(userData.email);
      } catch (err) {
        setError("Failed to load user data. Please try again.");
      }
    };
    fetchUserData();
  }, [userId]);

  const firstNameValid = validateName(firstName);
  const lastNameValid = validateName(lastName);
  const emailValid = validateEmail(email);

  const isFormValid = firstNameValid && lastNameValid && emailValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) return;

    setLoading(true);
    setError(null);
    setSuccess(false);
    setRedirecting(false);

    try {
      await handleUpdateUser();
      await handleCheckout();
    } catch (err) {
      setError(
        "Failed to update user data or start payment. Please try again."
      );
      setRedirecting(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!userId) {
      setError("User ID not found in URL.");
      return;
    }
    try {
      await userService.updateUser(userId, {
        first_name: firstName,
        last_name: lastName,
        email: email,
      });
      setSuccess(true);
    } catch (err) {
      setError("Failed to update user data. Please try again.");
    }
  };

  const handleCheckout = async () => {
    if (!userId) {
      setError("User ID not found in URL.");
      return;
    }
    try {
      const checkout = await userService.createCheckoutSession(
        userId,
        "+233244465077",
        5000
      );
      setRedirecting(true);
      window.location.href = checkout.checkout_url;
    } catch (err) {
      setError("Failed to create checkout session. Please try again.");
      setRedirecting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4">
      <form
        className="w-full max-w-md bg-white rounded-lg flex flex-col items-center p-4 md:p-8 shadow-none"
        onSubmit={handleSubmit}
        noValidate
      >
        <img
          src="/logo.svg"
          alt="Plus2 Logo"
          className="mx-auto mb-6"
          style={{ width: "120px", maxWidth: "80vw" }}
        />
        <h1 className="text-2xl md:text-3xl font-bold text-textPrimary text-center mb-2">
          One-Time Payment Setup
        </h1>
        <p className="text-secondary text-center mb-6 text-base md:text-lg">
          We'll send you your receipts via the email you provide
        </p>
        <div className="flex flex-col md:flex-row w-full gap-4 mb-4">
          <div className="flex-1">
            <label
              htmlFor="firstName"
              className="block text-textSecondary text-sm mb-1"
            >
              First name
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              aria-label="First name"
              className={`w-full rounded-md border px-4 py-3 text-base md:text-lg focus:outline-none transition-colors ${
                touched.firstName && !firstNameValid
                  ? "border-error"
                  : touched.firstName && firstNameValid
                  ? "border-inputFocus bg-gray-100"
                  : "border-inputBorder bg-gray-50"
              }`}
              placeholder="Enter first name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, firstName: true }))}
              minLength={2}
              required
            />
            {touched.firstName && !firstNameValid && (
              <div className="text-error text-xs mt-1">
                First name must be at least 2 alphabetic characters.
              </div>
            )}
          </div>
          <div className="flex-1">
            <label
              htmlFor="lastName"
              className="block text-textSecondary text-sm mb-1"
            >
              Last name
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              aria-label="Last name"
              className={`w-full rounded-md border px-4 py-3 text-base md:text-lg focus:outline-none transition-colors ${
                touched.lastName && !lastNameValid
                  ? "border-error"
                  : touched.lastName && lastNameValid
                  ? "border-inputFocus bg-gray-100"
                  : "border-inputBorder bg-gray-50"
              }`}
              placeholder="Enter last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, lastName: true }))}
              minLength={2}
              required
            />
            {touched.lastName && !lastNameValid && (
              <div className="text-error text-xs mt-1">
                Last name must be at least 2 alphabetic characters.
              </div>
            )}
          </div>
        </div>
        <div className="w-full mb-6">
          <label
            htmlFor="email"
            className="block text-textSecondary text-sm mb-1"
          >
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            aria-label="Email address"
            className={`w-full rounded-md border px-4 py-3 text-base md:text-lg focus:outline-none transition-colors ${
              touched.email && !emailValid
                ? "border-error"
                : touched.email && emailValid
                ? "border-inputFocus bg-gray-100"
                : "border-inputBorder bg-gray-50"
            }`}
            placeholder="Enter email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            required
          />
          {touched.email && !emailValid && (
            <div className="text-error text-xs mt-1">
              Please enter a valid email address.
            </div>
          )}
        </div>
        {error && (
          <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-error text-sm">
            {error}
          </div>
        )}

        {success && (
          <div className="w-full mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-600 text-sm">
            User information updated successfully!
          </div>
        )}

        {redirecting && (
          <div className="w-full mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-700 text-sm text-center">
            Redirecting you to Stripe to enter your card details. Please wait...
          </div>
        )}

        <button
          type="submit"
          className={`w-full h-12 rounded-md font-semibold text-lg transition-colors flex items-center justify-center mb-3
            ${
              isFormValid && !loading && !redirecting
                ? "bg-primary hover:bg-green-500 cursor-pointer"
                : "bg-buttonDisabled cursor-not-allowed"
            }
            text-white`}
          disabled={!isFormValid || loading || redirecting}
        >
          {loading || redirecting ? (
            <svg
              className="animate-spin h-6 w-6 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              ></path>
            </svg>
          ) : (
            "Setup Payment"
          )}
        </button>
        <p className="text-center text-sm mt-2 text-textSecondary">
          We use{" "}
          <a
            href="https://stripe.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-stripe font-semibold underline hover:text-indigo-700"
          >
            Stripe
          </a>{" "}
          to setup your payment securely.
        </p>
      </form>
    </div>
  );
};

export default App;
